/**
 * DiscrepancyInvestigation.jsx — iter207
 *
 * "Turn a variance into an investigation, not an adjustment."
 *
 * Two tabs:
 *   1. Investigate — enter (SKU/barcode) + (location) + physical count →
 *      full timeline, Discrepancy Detective, severity, confidence, serials,
 *      previous locations, and 'Find elsewhere'.
 *   2. Dashboard — by process / by location / by operator / recurring SKUs.
 *
 * Read-only over the discrepancy_investigation router. Never mutates stock;
 * the point is to give managers evidence before they adjust.
 */
import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  MagnifyingGlass, MapPin, Package, Warning, WarningCircle, CheckCircle,
  ChartBar, ClipboardText, Clock, ArrowClockwise, Detective, Bell, ArrowsLeftRight,
  FolderOpen, ArrowRight, Target, Kanban,
} from '@phosphor-icons/react';
import {
  investigateDiscrepancy, findStockElsewhere, getDiscrepancyStats,
  lookupLocationByCode, lookupItemByCode,
  getSuggestedCounts, listDiscrepancyAlerts, dismissDiscrepancyAlert,
  correctLocation, runDiscrepancyWatcher,
  openCase, transitionCase, listCases, getLocationAccuracy, seedCycleCountsFromSuggestions,
} from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import DiscrepancyBoard from '../components/DiscrepancyBoard';

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SeverityChip = ({ sev }) => {
  if (!sev) return null;
  const colors = { red: 'bg-red-100 text-red-800 border-red-300', amber: 'bg-amber-100 text-amber-800 border-amber-300', green: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
  return <Badge className={colors[sev.color] || ''} data-testid={`severity-${sev.tier}`}>{sev.label}</Badge>;
};

const ConfidenceChip = ({ conf }) => {
  if (!conf) return null;
  const colors = { red: 'bg-red-100 text-red-800 border-red-300', amber: 'bg-amber-100 text-amber-800 border-amber-300', green: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
  return (
    <Badge className={colors[conf.band?.color] || ''} data-testid="confidence-chip">
      {conf.band?.label} · {conf.score}
    </Badge>
  );
};

const TimelineRow = ({ mv }) => {
  const arrow = mv.movement_type === 'transfer_out' ? '→' : mv.movement_type === 'transfer_in' ? '←' : '·';
  return (
    <li className="flex items-start gap-3 py-2 border-b border-border/60 text-xs" data-testid={`timeline-${mv.id}`}>
      <div className="w-28 shrink-0 tabular-nums text-muted-foreground">
        {(mv.timestamp || '').slice(0, 10)}
      </div>
      <Badge variant="outline" className="text-[9px] shrink-0">{mv.process}</Badge>
      <div className="flex-1">
        <div className="font-medium">
          {mv.movement_type} {arrow} {mv.quantity > 0 ? '+' : ''}{mv.quantity}
          {mv.from_location_code && <span className="text-muted-foreground"> · {mv.from_location_code}</span>}
          {mv.to_location_code && <span className="text-muted-foreground"> → {mv.to_location_code}</span>}
          {mv.location_code && !mv.from_location_code && <span className="text-muted-foreground"> · {mv.location_code}</span>}
        </div>
        {mv.note && <div className="text-muted-foreground italic mt-0.5">{mv.note}</div>}
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {mv.user_email || mv.user_name || 'system'} · ref {mv.reference_type || '—'}/{mv.reference_id?.slice(-8) || '—'}
        </div>
      </div>
    </li>
  );
};

// --------------------------------- Investigate tab -------------------------
const InvestigatePanel = () => {
  const [skuInput, setSkuInput] = useState('');
  const [locInput, setLocInput] = useState('');
  const [physicalQty, setPhysicalQty] = useState('');
  const [resolvedVariant, setResolvedVariant] = useState(null);
  const [resolvedLocation, setResolvedLocation] = useState(null);
  const [result, setResult] = useState(null);
  const [elsewhere, setElsewhere] = useState(null);
  const [loading, setLoading] = useState(false);

  // Location-correction dialog state
  const [correctTarget, setCorrectTarget] = useState(null); // { fromLocId, fromCode, toLocId, toCode, availableThere }
  const [correctQty, setCorrectQty] = useState('');
  const [correctNote, setCorrectNote] = useState('');
  const [correctBusy, setCorrectBusy] = useState(false);

  // iter209: state-machine case tracking
  const [openCaseDoc, setOpenCaseDoc] = useState(null);
  const [caseBusy, setCaseBusy] = useState(false);
  const [transDialog, setTransDialog] = useState(null); // { target_state }
  const [transReason, setTransReason] = useState('');
  const [transNote, setTransNote] = useState('');
  const [reasonCatalog, setReasonCatalog] = useState({});
  const [allowedTrans, setAllowedTrans] = useState({});

  const resolveInputs = async () => {
    try {
      const [itemR, locR] = await Promise.all([
        lookupItemByCode(skuInput.trim()),
        lookupLocationByCode(locInput.trim()),
      ]);
      const variants = itemR.data?.variants || (itemR.data?.item ? [itemR.data.item] : []);
      const v = variants[0];
      const l = locR.data?.location || locR.data;
      if (!v || !l?.id) {
        toast.error('Could not resolve SKU or location');
        return null;
      }
      setResolvedVariant(v);
      setResolvedLocation(l);
      return { variant_id: v.id, location_id: l.id };
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Lookup failed');
      return null;
    }
  };

  const runInvestigation = async () => {
    if (!skuInput.trim() || !locInput.trim() || physicalQty === '') {
      toast.error('SKU, location and physical count are all required');
      return;
    }
    setLoading(true);
    setResult(null); setElsewhere(null); setOpenCaseDoc(null);
    try {
      const ids = await resolveInputs();
      if (!ids) { setLoading(false); return; }
      const r = await investigateDiscrepancy({ ...ids, physical_qty: Number(physicalQty) });
      setResult(r.data);
      if (r.data.variance !== 0) {
        const fe = await findStockElsewhere(ids.variant_id, ids.location_id);
        setElsewhere(fe.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Investigation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Query form */}
      <Card className="p-4" data-testid="investigate-form">
        <p className="overline mb-3">Start an investigation</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">SKU code / barcode</Label>
            <Input
              value={skuInput}
              onChange={(e) => setSkuInput(e.target.value)}
              placeholder="SKU-ABC123"
              data-testid="input-sku"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Location code</Label>
            <Input
              value={locInput}
              onChange={(e) => setLocInput(e.target.value)}
              placeholder="A-01-02"
              data-testid="input-location"
            />
          </div>
          <div>
            <Label className="text-xs">Physical count</Label>
            <Input
              type="number" min={0}
              value={physicalQty}
              onChange={(e) => setPhysicalQty(e.target.value)}
              placeholder="42"
              data-testid="input-physical-qty"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={runInvestigation}
              disabled={loading}
              className="w-full bg-brand hover:bg-brand/90"
              data-testid="btn-investigate"
            >
              <MagnifyingGlass size={14} className="mr-1" />
              {loading ? 'Investigating…' : 'Investigate'}
            </Button>
          </div>
        </div>
      </Card>

      {result && (
        <>
          {/* Headline card */}
          <Card className="p-4 border-l-4" style={{ borderLeftColor: result.severity?.color === 'red' ? '#dc2626' : result.severity?.color === 'amber' ? '#f59e0b' : '#16a34a' }} data-testid="result-headline">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Package size={14} className="text-brand" weight="fill" />
                  <span className="text-xs font-bold uppercase tracking-widest">{result.variant.sku_code}</span>
                  <SeverityChip sev={result.severity} />
                  <ConfidenceChip conf={result.confidence} />
                </div>
                <h3 className="text-lg font-bold">{result.variant.name}</h3>
                <p className="text-xs text-muted-foreground">
                  UOM: {result.variant.uom} · Batch: {result.variant.batch || '—'} · Location: {result.location.location_code} · Zone: {result.location.zone || '—'}
                </p>
              </div>
              <div className="grid grid-cols-4 gap-3 text-center text-xs">
                <div>
                  <div className="overline">System</div>
                  <div className="text-2xl font-bold tabular-nums">{result.system_qty}</div>
                </div>
                <div>
                  <div className="overline">Physical</div>
                  <div className="text-2xl font-bold tabular-nums">{result.physical_qty}</div>
                </div>
                <div>
                  <div className="overline">Variance</div>
                  <div className={`text-2xl font-bold tabular-nums ${result.variance < 0 ? 'text-red-600' : result.variance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {result.variance > 0 ? '+' : ''}{result.variance}
                  </div>
                </div>
                <div>
                  <div className="overline">$ value</div>
                  <div className="text-2xl font-bold tabular-nums">{money(Math.abs(result.variance_value))}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Discrepancy detective */}
          <Card className="p-4 bg-brand/5 border-brand/20" data-testid="detective-card">
            <div className="flex items-center gap-2 mb-3">
              <Detective size={16} className="text-brand" weight="fill" />
              <h4 className="text-sm font-bold uppercase tracking-widest">Discrepancy Detective</h4>
              {result.last_certain_point && (
                <Badge variant="outline" className="text-[10px]">
                  Last certain: {result.last_certain_point.label} · {(result.last_certain_point.timestamp || '').slice(0, 10)}
                </Badge>
              )}
            </div>
            <ul className="space-y-2">
              {(result.detective?.likely_causes || []).map((c, i) => (
                <li key={i} className="border border-brand/20 bg-white dark:bg-slate-900 p-3" data-testid={`cause-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[9px]">{c.confidence}</Badge>
                    <span className="font-bold text-sm">{c.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{c.detail}</p>
                  <p className="text-xs mt-1"><strong>Recommended:</strong> {c.recommended_action}</p>
                </li>
              ))}
            </ul>
          </Card>

          {/* Find elsewhere */}
          {elsewhere && (
            <Card className="p-4" data-testid="find-elsewhere-card">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-brand" weight="fill" />
                <h4 className="text-sm font-bold uppercase tracking-widest">Found elsewhere</h4>
                <Badge variant="outline">{elsewhere.matches?.length || 0} other locations</Badge>
                <span className="text-[10px] text-muted-foreground ml-2">
                  Missing? These bins may hold the units — verify then click <strong>Move here</strong>.
                </span>
              </div>
              {(elsewhere.matches || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No units of this SKU in any other location.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-left px-2 py-1">Location</th>
                      <th className="text-left px-2 py-1">Zone</th>
                      <th className="text-right px-2 py-1">On hand</th>
                      <th className="text-right px-2 py-1">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {elsewhere.matches.map((m) => (
                      <tr key={m.location_id} className="border-t border-border/40">
                        <td className="px-2 py-1 font-mono">{m.location_code}</td>
                        <td className="px-2 py-1">{m.zone || '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{m.quantity}</td>
                        <td className="px-2 py-1 text-right">
                          <Button
                            size="sm" variant="outline" className="h-6 text-[10px]"
                            data-testid={`move-here-${m.location_id}`}
                            onClick={() => {
                              setCorrectTarget({
                                fromLocId: m.location_id, fromCode: m.location_code,
                                toLocId: result.location.id, toCode: result.location.location_code,
                                availableThere: m.quantity, defaultQty: Math.min(m.quantity, Math.max(0, -result.variance)),
                              });
                              setCorrectQty(String(Math.min(m.quantity, Math.max(0, -result.variance)) || 1));
                              setCorrectNote('Units located during discrepancy investigation');
                            }}
                          >
                            <ArrowsLeftRight size={11} className="mr-1" /> Move here
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}

          {/* Previous locations */}
          {(result.previous_locations || []).length > 0 && (
            <Card className="p-4" data-testid="previous-locations">
              <p className="overline mb-2">Previous locations (last 90d)</p>
              <div className="flex gap-2 flex-wrap">
                {result.previous_locations.map((l, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">
                    {l.location_code} · {(l.last_seen || '').slice(0, 10)}
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Serials */}
          {result.serials_at_location?.length > 0 && (
            <Card className="p-4" data-testid="serials-card">
              <p className="overline mb-2">Serials at this location ({result.serials_at_location.length})</p>
              <div className="max-h-40 overflow-auto text-xs font-mono grid grid-cols-2 md:grid-cols-4 gap-1">
                {result.serials_at_location.map((s) => (
                  <span key={s.serial_no} className="bg-muted/30 px-2 py-0.5">{s.serial_no}</span>
                ))}
              </div>
            </Card>
          )}

          {/* Timeline */}
          <Card className="p-4" data-testid="timeline-card">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-brand" weight="fill" />
              <h4 className="text-sm font-bold uppercase tracking-widest">Transaction timeline</h4>
              <Badge variant="outline">{result.timeline?.length || 0} events</Badge>
            </div>
            {(result.timeline || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No movements recorded for this SKU + location.</p>
            ) : (
              <ul className="max-h-96 overflow-auto">
                {result.timeline.map((mv) => <TimelineRow key={mv.id} mv={mv} />)}
              </ul>
            )}
          </Card>

          <p className="text-[10px] italic text-muted-foreground">
            Stockaris does not auto-adjust the ledger on investigation. If you believe the variance is real,
            proceed via Cycle Counts → Approve so the movement carries a reason + audit chain.
          </p>

          {/* iter209: Enforced state machine — Investigation case */}
          <Card className="p-4 border-l-4 border-brand" data-testid="case-card">
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen size={16} className="text-brand" weight="fill" />
              <p className="overline">Investigation case</p>
              {openCaseDoc && (
                <>
                  <Badge className="uppercase text-[10px]" data-testid="case-state">{openCaseDoc.state}</Badge>
                  <span className="text-[10px] text-muted-foreground">
                    {openCaseDoc.case_number} · opened {(openCaseDoc.opened_at || '').slice(0, 10)}
                  </span>
                </>
              )}
              {!openCaseDoc && result?.variance !== 0 && (
                <Button
                  size="sm" className="ml-auto bg-brand hover:bg-brand/90"
                  disabled={caseBusy}
                  data-testid="open-case-btn"
                  onClick={async () => {
                    setCaseBusy(true);
                    try {
                      const r = await openCase({
                        variant_id: result.variant.id,
                        location_id: result.location.id,
                        system_qty: result.system_qty,
                        physical_qty: result.physical_qty,
                        initial_note: 'Opened from Discrepancy Investigation',
                      });
                      setOpenCaseDoc(r.data);
                      // load transitions catalogue
                      const listR = await listCases();
                      setReasonCatalog(listR.data.reason_codes || {});
                      setAllowedTrans(listR.data.allowed_transitions || {});
                      toast.success(`Case ${r.data.case_number} opened`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Failed to open case');
                    } finally {
                      setCaseBusy(false);
                    }
                  }}
                >
                  <FolderOpen size={12} className="mr-1" /> Open investigation case
                </Button>
              )}
            </div>

            {openCaseDoc ? (
              <>
                {/* State progression */}
                <div className="flex items-center gap-1 flex-wrap text-[10px] mb-2">
                  {['new', 'investigating', 'root_cause_assigned', 'corrected', 'approved', 'closed'].map((s, i, arr) => {
                    const passed = arr.indexOf(openCaseDoc.state) >= i;
                    return (
                      <React.Fragment key={s}>
                        <span className={`px-2 py-0.5 rounded-sm font-mono uppercase ${passed ? 'bg-brand text-white' : 'bg-muted text-muted-foreground'}`}>
                          {s.replace(/_/g, ' ')}
                        </span>
                        {i < arr.length - 1 && <ArrowRight size={10} className="text-muted-foreground" />}
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Transition buttons */}
                {(allowedTrans[openCaseDoc.state] || []).length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-3">
                    {(allowedTrans[openCaseDoc.state] || []).map((s) => (
                      <Button
                        key={s} size="sm" variant="outline" className="text-[10px]"
                        data-testid={`trans-${s}`}
                        onClick={() => { setTransDialog({ target_state: s }); setTransReason((reasonCatalog[s] || [''])[0] || ''); setTransNote(''); }}
                      >
                        → {s.replace(/_/g, ' ')}
                      </Button>
                    ))}
                  </div>
                )}

                {/* History */}
                <div className="max-h-40 overflow-auto border-t border-border/40 pt-2" data-testid="case-history">
                  <p className="overline mb-1">Case history</p>
                  {(openCaseDoc.history || []).map((h, i) => (
                    <div key={i} className="text-[11px] py-1 border-b border-border/40 last:border-none">
                      <Badge variant="outline" className="text-[9px] mr-2">{h.state}</Badge>
                      <span className="font-mono text-[10px]">{h.reason_code}</span>
                      <span className="text-muted-foreground ml-2">· {h.actor} · {(h.at || '').slice(0, 16).replace('T', ' ')}</span>
                      {h.note && <div className="text-muted-foreground italic pl-2 mt-0.5">"{h.note}"</div>}
                    </div>
                  ))}
                </div>
              </>
            ) : result?.variance === 0 ? (
              <p className="text-xs text-muted-foreground">Zero variance — no case needed.</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Open an investigation case to lock the COUNT → VARIANCE → INVESTIGATE → ROOT CAUSE → CORRECT → APPROVE workflow with reason codes at every hop.
              </p>
            )}
          </Card>
        </>
      )}

      {/* Before/after location correction dialog */}
      <Dialog open={!!correctTarget} onOpenChange={(o) => !o && setCorrectTarget(null)}>
        <DialogContent data-testid="correct-location-dialog">
          <DialogHeader>
            <DialogTitle>Location correction — move stock, don't adjust</DialogTitle>
            <DialogDescription className="text-xs">
              This records a paired <code>location_correction_out</code> / <code>location_correction_in</code>
              movement so total on-hand is preserved. It's logged as a location-accuracy fix, not shrinkage.
            </DialogDescription>
          </DialogHeader>
          {correctTarget && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-border p-3">
                  <div className="overline">From</div>
                  <div className="font-mono text-lg">{correctTarget.fromCode}</div>
                  <div className="text-[10px] text-muted-foreground">Available: {correctTarget.availableThere}</div>
                </div>
                <div className="border border-brand/40 p-3 bg-brand/5">
                  <div className="overline">To (investigated location)</div>
                  <div className="font-mono text-lg">{correctTarget.toCode}</div>
                  <div className="text-[10px] text-muted-foreground">Current variance: {result?.variance}</div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Quantity to move</Label>
                <Input
                  type="number" min={1} max={correctTarget.availableThere}
                  value={correctQty} onChange={(e) => setCorrectQty(e.target.value)}
                  data-testid="correct-qty-input"
                />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input
                  value={correctNote} onChange={(e) => setCorrectNote(e.target.value)}
                  data-testid="correct-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectTarget(null)} disabled={correctBusy}>Cancel</Button>
            <Button
              className="bg-brand hover:bg-brand/90"
              disabled={correctBusy || !correctQty}
              data-testid="confirm-correct-btn"
              onClick={async () => {
                if (!correctTarget || !result) return;
                setCorrectBusy(true);
                try {
                  const r = await correctLocation({
                    variant_id: result.variant.id,
                    from_location_id: correctTarget.fromLocId,
                    to_location_id: correctTarget.toLocId,
                    quantity: Number(correctQty),
                    note: correctNote,
                  });
                  toast.success(`Moved ${correctQty} from ${correctTarget.fromCode} → ${correctTarget.toCode}`);
                  setCorrectTarget(null);
                  // Re-run investigation so the report reflects the correction
                  await runInvestigation();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Correction failed');
                } finally {
                  setCorrectBusy(false);
                }
              }}
            >
              {correctBusy ? 'Moving…' : 'Confirm move'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter209: transition dialog */}
      <Dialog open={!!transDialog} onOpenChange={(o) => !o && setTransDialog(null)}>
        <DialogContent data-testid="trans-dialog">
          <DialogHeader>
            <DialogTitle>
              Advance case → <span className="uppercase font-mono text-brand">{transDialog?.target_state?.replace(/_/g, ' ')}</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              A reason code is required at every hop. The transition is audit-logged with your identity + timestamp.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Reason code</Label>
              <select
                className="w-full h-9 border border-input rounded-md px-2 text-sm bg-background"
                value={transReason}
                onChange={(e) => setTransReason(e.target.value)}
                data-testid="trans-reason-select"
              >
                {(reasonCatalog[transDialog?.target_state] || ['unspecified']).map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input value={transNote} onChange={(e) => setTransNote(e.target.value)} data-testid="trans-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransDialog(null)} disabled={caseBusy}>Cancel</Button>
            <Button
              className="bg-brand hover:bg-brand/90"
              disabled={caseBusy || !transReason}
              data-testid="confirm-trans-btn"
              onClick={async () => {
                if (!openCaseDoc || !transDialog) return;
                setCaseBusy(true);
                try {
                  const r = await transitionCase(openCaseDoc.id, {
                    target_state: transDialog.target_state,
                    reason_code: transReason,
                    note: transNote,
                  });
                  setOpenCaseDoc(r.data);
                  toast.success(`Case advanced → ${transDialog.target_state}`);
                  setTransDialog(null);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Transition rejected');
                } finally {
                  setCaseBusy(false);
                }
              }}
            >
              {caseBusy ? 'Saving…' : 'Confirm transition'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --------------------------------- Dashboard tab ---------------------------
const DashboardPanel = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [suggested, setSuggested] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [watcherBusy, setWatcherBusy] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);
  const [locAccuracy, setLocAccuracy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsR, sugR, alertsR, laR] = await Promise.all([
        getDiscrepancyStats(days),
        getSuggestedCounts(15),
        listDiscrepancyAlerts('active'),
        getLocationAccuracy(days),
      ]);
      setData(statsR.data);
      setSuggested(sugR.data);
      setAlerts(alertsR.data);
      setLocAccuracy(laR.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const runWatcher = async () => {
    setWatcherBusy(true);
    try {
      const r = await runDiscrepancyWatcher();
      toast.success(`Watcher ran · ${r.data.alerts_emitted} new alert(s) · ${r.data.scanned_variants} SKUs scanned`);
      const alertsR = await listDiscrepancyAlerts('active');
      setAlerts(alertsR.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Watcher failed');
    } finally {
      setWatcherBusy(false);
    }
  };

  const dismiss = async (alertId) => {
    try {
      await dismissDiscrepancyAlert(alertId);
      setAlerts({ ...alerts, alerts: alerts.alerts.filter((a) => a.id !== alertId), count: alerts.count - 1 });
      toast.success('Alert dismissed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dismiss failed');
    }
  };

  useEffect(() => { load(); }, [load]);

  const h = data?.headline || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs">Window</Label>
        <Input
          type="number" min={1} max={365} value={days}
          onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
          className="w-20" data-testid="stats-days"
        />
        <span className="text-xs text-muted-foreground">days</span>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="stats-refresh">
          <ArrowClockwise size={12} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stats-headline">
        <Card className="p-3">
          <div className="overline">Total events</div>
          <div className="text-2xl font-bold tabular-nums">{h.total_events ?? 0}</div>
        </Card>
        <Card className="p-3">
          <div className="overline">$ variance</div>
          <div className="text-2xl font-bold tabular-nums">{money(h.total_variance_value)}</div>
        </Card>
        <Card className="p-3">
          <div className="overline">Recurring SKUs</div>
          <div className="text-2xl font-bold tabular-nums">{h.recurring_skus ?? 0}</div>
        </Card>
        <Card className="p-3">
          <div className="overline">Unique locations</div>
          <div className="text-2xl font-bold tabular-nums">{h.unique_locations ?? 0}</div>
        </Card>
        <Card className="p-3 border-l-4" style={{ borderLeftColor: locAccuracy?.band?.color === 'red' ? '#dc2626' : locAccuracy?.band?.color === 'amber' ? '#f59e0b' : '#16a34a' }} data-testid="location-accuracy-tile">
          <div className="overline flex items-center gap-1"><Target size={10} /> Location accuracy</div>
          <div className="text-2xl font-bold tabular-nums">{locAccuracy?.accuracy_pct?.toFixed(1) ?? '—'}%</div>
          <div className="text-[9px] text-muted-foreground">
            {locAccuracy?.corrections ?? 0} corrections / {locAccuracy?.picks ?? 0} picks · {locAccuracy?.band?.label}
          </div>
        </Card>
      </div>

      {/* Active recurring-discrepancy alerts */}
      <Card className="p-4 border-l-4 border-red-500" data-testid="active-alerts-card">
        <div className="flex items-center gap-2 mb-2">
          <Bell size={16} className="text-red-600" weight="fill" />
          <p className="overline">Active recurring-discrepancy alerts</p>
          <Badge variant="outline" className="text-[10px]">{alerts?.count ?? 0}</Badge>
          <Button
            size="sm" variant="outline" className="ml-auto"
            onClick={runWatcher} disabled={watcherBusy}
            data-testid="run-watcher-btn"
          >
            <ArrowClockwise size={12} className={`mr-1 ${watcherBusy ? 'animate-spin' : ''}`} />
            Run watcher now
          </Button>
        </div>
        {(alerts?.alerts || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No active alerts. Watcher runs hourly + on demand.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-2 py-1">SKU</th>
                <th className="text-left px-2 py-1">Name</th>
                <th className="text-right px-2 py-1">Variances / {alerts.alerts[0]?.window_days || 30}d</th>
                <th className="text-right px-2 py-1">|Δ| units</th>
                <th className="text-right px-2 py-1">$ value</th>
                <th className="text-left px-2 py-1">Severity</th>
                <th className="text-right px-2 py-1">Fired</th>
                <th className="text-right px-2 py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {alerts.alerts.map((a) => (
                <tr key={a.id} className="border-t border-border/40" data-testid={`alert-${a.id}`}>
                  <td className="px-2 py-1 font-mono">{a.sku_code}</td>
                  <td className="px-2 py-1">{a.sku_name}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-bold">{a.count_in_window}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{a.abs_qty_total}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(a.value_total)}</td>
                  <td className="px-2 py-1">
                    <Badge className={a.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                      {a.severity}
                    </Badge>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{(a.created_at || '').slice(0, 10)}</td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                      onClick={() => dismiss(a.id)} data-testid={`dismiss-${a.id}`}>
                      Dismiss
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Suggested cycle counts */}
      <Card className="p-4 border-l-4 border-brand" data-testid="suggested-counts-card">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Detective size={16} className="text-brand" weight="fill" />
          <p className="overline">Today's suggested counts — risk-scored</p>
          <Badge variant="outline" className="text-[10px]">{suggested?.count ?? 0}</Badge>
          <span className="text-[10px] text-muted-foreground ml-2">
            Confidence × $ value × adjustment history × time since count × transaction volume
          </span>
          <Button
            size="sm" className="ml-auto bg-brand hover:bg-brand/90"
            disabled={seedBusy || !(suggested?.suggestions?.length)}
            data-testid="seed-counts-btn"
            onClick={async () => {
              setSeedBusy(true);
              try {
                const r = await seedCycleCountsFromSuggestions({ limit: 20 });
                toast.success(`${r.data.count_number} created · ${r.data.skus_seeded} SKUs · ${r.data.locations_targeted} locations`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Failed to seed counts');
              } finally {
                setSeedBusy(false);
              }
            }}
          >
            <ClipboardText size={12} className="mr-1" /> Create today's counts (top 20)
          </Button>
        </div>
        {(suggested?.suggestions || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No SKUs need counting right now.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-2 py-1">Risk</th>
                <th className="text-left px-2 py-1">SKU</th>
                <th className="text-left px-2 py-1">Name</th>
                <th className="text-right px-2 py-1">On hand</th>
                <th className="text-right px-2 py-1">$ value</th>
                <th className="text-right px-2 py-1">Conf.</th>
                <th className="text-left px-2 py-1">Why it's here</th>
              </tr>
            </thead>
            <tbody>
              {suggested.suggestions.map((r) => (
                <tr key={r.variant_id} className="border-t border-border/40" data-testid={`suggested-${r.variant_id}`}>
                  <td className="px-2 py-1 tabular-nums">
                    <span className={`px-1.5 py-0.5 rounded-sm font-bold ${
                      r.risk_score >= 60 ? 'bg-red-100 text-red-800' :
                      r.risk_score >= 40 ? 'bg-amber-100 text-amber-800' :
                      'bg-emerald-100 text-emerald-800'}`}>{r.risk_score}</span>
                  </td>
                  <td className="px-2 py-1 font-mono">{r.sku_code}</td>
                  <td className="px-2 py-1">{r.name}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{r.on_hand}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(r.value)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{r.confidence_score}</td>
                  <td className="px-2 py-1 text-[11px] text-muted-foreground">{r.why.join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* By process */}
      <Card className="p-4" data-testid="by-process">
        <p className="overline mb-2">Where errors originate (by process)</p>
        {(data?.by_process || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No discrepancies in this window.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr><th className="text-left px-2 py-1">Process</th><th className="text-right px-2 py-1">Events</th><th className="text-right px-2 py-1">$ value</th></tr>
            </thead>
            <tbody>
              {data.by_process.map((r) => (
                <tr key={r.process} className="border-t border-border/40">
                  <td className="px-2 py-1"><Badge variant="outline">{r.process}</Badge></td>
                  <td className="px-2 py-1 text-right tabular-nums">{r.occurrences}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By location */}
        <Card className="p-4" data-testid="by-location">
          <p className="overline mb-2">Worst locations</p>
          {(data?.by_location || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No discrepancies.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/30">
                <tr><th className="text-left px-2 py-1">Location</th><th className="text-right px-2 py-1">Events</th><th className="text-right px-2 py-1">|Δ|</th><th className="text-right px-2 py-1">$</th></tr>
              </thead>
              <tbody>
                {data.by_location.slice(0, 20).map((r) => (
                  <tr key={r.location_code} className="border-t border-border/40">
                    <td className="px-2 py-1 font-mono">{r.location_code}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.discrepancies}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.abs_qty}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{money(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* By operator */}
        <Card className="p-4" data-testid="by-operator">
          <p className="overline mb-2">By operator (training signal)</p>
          {(data?.by_operator || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No operator-attributed discrepancies.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/30">
                <tr><th className="text-left px-2 py-1">Person</th><th className="text-right px-2 py-1">Events</th><th className="text-right px-2 py-1">|Δ|</th><th className="text-right px-2 py-1">$</th></tr>
              </thead>
              <tbody>
                {data.by_operator.slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="px-2 py-1">{r.name}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.discrepancies}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.abs_qty}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{money(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Recurring SKUs */}
      <Card className="p-4" data-testid="recurring-skus">
        <p className="overline mb-2">Recurring discrepancies (SKUs with 2+ events)</p>
        {(data?.recurring_skus || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No SKU has repeated discrepancies in this window.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr><th className="text-left px-2 py-1">SKU</th><th className="text-left px-2 py-1">Name</th><th className="text-right px-2 py-1">Events</th></tr>
            </thead>
            <tbody>
              {data.recurring_skus.slice(0, 25).map((r) => (
                <tr key={r.variant_id} className="border-t border-border/40">
                  <td className="px-2 py-1 font-mono">{r.sku_code}</td>
                  <td className="px-2 py-1">{r.name}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-bold">{r.discrepancy_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <p className="text-[10px] italic text-muted-foreground">
        Discrepancies are inferred from `cycle_count_adjust` movements with non-zero variance in the window.
      </p>
    </div>
  );
};

// --------------------------------- Page shell ------------------------------
export default function DiscrepancyInvestigation() {
  return (
    <Layout>
      <div className="space-y-4" data-testid="discrepancy-investigation-page">
        <div>
          <p className="overline">Inventory quality</p>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Detective size={24} className="text-brand" weight="fill" />
            Stock Discrepancy Investigation
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
            Turn a variance into an investigation, not an adjustment. Enter a SKU + location + physical count
            and Stockaris returns the transaction timeline, likely-cause detective, severity, confidence and
            'find elsewhere' suggestions.
          </p>
        </div>
        <Tabs defaultValue="investigate">
          <TabsList data-testid="disc-tabs">
            <TabsTrigger value="investigate" data-testid="tab-investigate">
              <MagnifyingGlass size={14} className="mr-1" /> Investigate
            </TabsTrigger>
            <TabsTrigger value="board" data-testid="tab-board">
              <Kanban size={14} className="mr-1" /> Board
            </TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <ChartBar size={14} className="mr-1" /> Dashboard
            </TabsTrigger>
          </TabsList>
          <TabsContent value="investigate" className="mt-4">
            <InvestigatePanel />
          </TabsContent>
          <TabsContent value="board" className="mt-4">
            <DiscrepancyBoard />
          </TabsContent>
          <TabsContent value="dashboard" className="mt-4">
            <DashboardPanel />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
